import DOMPurify from "dompurify";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "preact/hooks";
import {
  blobText,
  fetchPage,
  noteHtml,
  required,
  restoreScroll,
  styled,
  ToucanButton,
  ToucanEmptyNote,
  ToucanHeaderAction,
  ToucanKeyCap,
  ToucanSpinner,
  useKeyboardInUse,
} from "toucan";
import { pdfSheets } from "../pdf.ts";
import {
  archiveItem,
  framableHere,
  isArchived,
  itemReadingTime,
  markLostFigures,
  markRead,
  missingSentence,
  openUrl,
  publishedLabel,
  siteFromUrl,
} from "../model.ts";
import { notePlacing, placeIn, READ_INTO, rememberPlace } from "../place.ts";
import { go, libraryPath } from "../routes.ts";
import type { JSX } from "preact";
import type { Item } from "../model.ts";

/**
 * What the link to the site itself is called, wherever it is offered. The
 * same word in an installed window, which can only reach the site through
 * the system's share sheet, since that is what opening a page is there.
 */
const WAY_OUT = "↗ Open";

/**
 * The link to the site itself, in the reading screen's header so a long
 * document never has to be scrolled back up to leave it. It opens a new tab,
 * and marks the item read.
 */
export function ReadingAction({ item }: { item: Item }) {
  return (
    <ToucanHeaderAction
      class="original"
      // The site's own address, even when paywalled, for a reader who
      // subscribes. The archive is offered where the reading would have been.
      href={item.url}
      onClick={() => {
        markRead(item, Date.now());
      }}
    >
      {WAY_OUT}
    </ToucanHeaderAction>
  );
}

/** The way out's label where it leads to a snapshot rather than a site. */
const ARCHIVED_COPY = "↗ Read the archived copy";

/**
 * The way out's label where the site told the proxy it had nothing, since a
 * browser can still be let in, or sent on to where the thing moved.
 */
const ANYWAY = "↗ Open anyway";

/**
 * The link to the original, drawn large in place of a reading pane that has
 * nothing to show. For a paywalled page it goes to an archived snapshot, and
 * its label says so.
 */
function OpenOriginal({ item }: { item: Item }) {
  const archived = item.paywalled === true;
  const label = archived
    ? ARCHIVED_COPY
    : item.missing === undefined
      ? WAY_OUT
      : ANYWAY;
  return (
    <a
      class="original"
      href={openUrl(item)}
      target="_blank"
      rel="noreferrer"
      onClick={() => {
        markRead(item, Date.now());
      }}
    >
      {label}
    </a>
  );
}

/**
 * The sentence for a reading pane with nothing to show. It gives the site's
 * refusal where there was one, and `otherwise` where there wasn't.
 */
function nothingSaid(item: Item, otherwise: string): string {
  return item.missing === undefined
    ? otherwise
    : missingSentence(item, item.missing);
}

const Page = styled("section")`
  padding: 1rem 1rem 4rem;

  /* The reading measure: long lines are the one thing that makes an otherwise
     clean extraction unreadable. */
  .prose {
    max-width: 38rem;
    margin: 0 auto;
    font-size: 1.05rem;
    line-height: 1.65;
    color: var(--toucan-ink-strong);
  }

  /* On a phone the measure is already the width of the screen, so the type
     can be larger without making lines too long. */
  @media (max-width: 719px) {
    .prose {
      font-size: 1.15rem;
    }
  }

  /* The article's own picture, where the reading does not already contain it,
     kept to the reading measure like the text. */
  .lead {
    display: block;
    width: 100%;
    max-width: 38rem;
    height: auto;
    margin: 0 auto 1.5rem;
    border-radius: var(--pico-border-radius);
    background: var(--toucan-surface-sunken);
    box-shadow: var(--toucan-shadow-sm);
  }

  /* The title and the rule that finishes the reading take the width of what
     is between them: the reading measure for an article, the whole screen for
     a framed site. */
  .headline,
  .finish {
    max-width: 38rem;
    margin-left: auto;
    margin-right: auto;
  }

  /* For a paper the rule is the width of its pages. The heading is not: a
     paper shows one only when it would not open, over a sentence at the
     prose measure. */
  &[data-kind="pdf"] .finish {
    max-width: 52rem;
  }

  /* A framed site's title and rule span the screen with the frame. A link
     that shows a note instead keeps the prose measure. */
  &[data-framed] .headline,
  &[data-framed] .finish {
    max-width: none;
  }

  h1 {
    /* A title from a URL slug can be one unbroken word wider than the screen,
       and a heading that runs off the edge takes the reading with it. */
    overflow-wrap: anywhere;
    margin: 0 0 0.25rem;
    font-size: 1.6rem;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: var(--toucan-heading);
  }

  .byline {
    margin: 0 0 1.5rem;
    color: var(--toucan-muted);
  }

  /* The article's own pictures, pointed at where they live. Held inside the
     measure, as are its videos and diagrams, so a wide one does not push the
     text sideways. */
  .prose :is(img, video, svg) {
    max-width: 100%;
    height: auto;
  }

  .prose img {
    border-radius: var(--pico-border-radius);
  }

  .prose figure {
    margin: 1.5rem 0;
  }

  /* Where a figure's picture never came across: a dashed outline, so it
     doesn't read as content, in the muted colour the byline has. */
  .prose .lost-figure {
    margin: 0 0 0.5rem;
    padding: 0.75rem 1rem;
    border: 1px dashed var(--toucan-card-border);
    border-radius: var(--pico-border-radius);
    color: var(--toucan-muted);
    font-size: 0.95rem;
  }

  .prose blockquote {
    margin: 1.5rem 0;
    padding-left: 1rem;
    border-left: 3px solid var(--toucan-card-border);
    color: var(--toucan-muted);
  }

  .prose pre {
    overflow-x: auto;
  }

  /* A page of a paper is a picture with its own margins, so it is wider than
     the prose measure, and each page is a card so the breaks show. */
  .pages {
    max-width: 52rem;
    margin: 0 auto;
  }

  .pages .sheets {
    display: grid;
    gap: 1rem;
  }

  /* The spinner while a reading loads, centred in a deep band. */
  .fetching {
    display: grid;
    place-items: center;
    padding: 4rem 0;
    color: var(--toucan-muted);
  }

  /* A page's box, sized to the page's proportions before it is drawn, so
     the reading doesn't move as later pages arrive. */
  .pages .sheet {
    width: 100%;
    border-radius: var(--pico-border-radius);
    background: var(--toucan-surface);
    box-shadow: var(--toucan-shadow-sm);
  }

  .pages canvas {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: inherit;
  }

  /* The site itself. It scrolls inside its own box, since a cross-origin
     frame's content height can't be measured. */
  .frame {
    width: 100%;
    height: 75vh;
    border: 1px solid var(--toucan-card-border);
    border-radius: var(--pico-border-radius);
    background: var(--toucan-surface);
  }

  /* The rule that ends the reading, with the note and actions under it, so
     finishing doesn't need a scroll back to the header. */
  .finish {
    margin-top: 2.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--toucan-card-border);
    display: grid;
    gap: 1rem;
  }

  /* The item's note, under the rule and above the actions. It has a label so
     it doesn't read as another paragraph of the article. A pasted address is
     one unbroken word, so it may break anywhere rather than widen the page. */
  .noted {
    overflow-wrap: anywhere;
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
    color: var(--toucan-ink);
  }

  /* The note's bold label. */
  .noted-marker {
    margin-bottom: 0.25rem;
    font-style: normal;
    font-size: 1rem;
    font-weight: 700;
  }

  .finish-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
  }

  /* The link to the original in place of the reading pane, filled and sized
     as a primary action since it is the only thing to tap. The arrow leads,
     as on Places' Maps link. */
  .original {
    display: inline-flex;
    align-items: center;
    gap: 0.1875rem;
    width: auto;
    margin: 1rem auto 0;
    padding: 0.5rem 1.1rem;
    border: 1px solid var(--pico-primary);
    border-radius: 999px;
    background: var(--pico-primary);
    color: var(--toucan-surface);
    font: inherit;
    font-size: 1rem;
    line-height: 1.4;
    white-space: nowrap;
    text-decoration: none;
    box-shadow: var(--toucan-shadow-sm);
  }

  .original:hover {
    background: var(--pico-primary-hover);
    color: var(--toucan-surface);
  }

  /* The note and the button under it, centred together. It keeps the reading
     measure even where it replaces a full-width frame or a paper's pages,
     because it is a sentence. */
  .nothing {
    display: grid;
    justify-items: center;
    max-width: 38rem;
    margin: 0 auto;
    text-align: center;
  }
`;

/** A reader's state, with the content or the reason it has none to show. */
type Loaded<T> =
  { state: "loading" } | { state: "missing" } | { state: "ready"; content: T };

/**
 * The stored text cleaned for the page, or undefined where this device does
 * not hold it and cannot reach the server to ask.
 *
 * The HTML came from somebody else's website, and an app on this origin can
 * read the sync token, so DOMPurify cleans it. Readability doesn't promise to
 * strip scripts.
 *
 * DOMPurify is imported statically, not on demand, because `precache.json`
 * lists only static imports and a chunk loaded by `import()` would not load
 * offline. The extractor imports it anyway, so this adds nothing to the
 * download.
 */
function readingText(
  hash: string,
  address: string,
): Promise<string | undefined> {
  return blobText(hash).then((stored) =>
    stored === undefined
      ? undefined
      : // Marked after the cleaning, so the sanitiser never touches the
        // markup this app adds.
        markLostFigures(DOMPurify.sanitize(stored), address),
  );
}

/**
 * Whether the reading already contains the lead picture, so it is not shown
 * above it too.
 *
 * The serialized HTML writes a query's `&` as `&amp;`, while the item's
 * address was read off a decoded attribute. `new URL` percent-encodes every
 * other character a serializer would escape, so undoing `&amp;` is all the
 * decoding the comparison needs.
 */
function readingCarries(html: string, address: string): boolean {
  return html.replaceAll("&amp;", "&").includes(address);
}

/**
 * An article's extracted text, once the page has loaded it. Text is stored
 * best effort, so text that nobody within reach holds is a state this draws,
 * not an error.
 */
function ArticleText({ item, text }: { item: Item; text: Loaded<string> }) {
  return (
    <div class="prose">
      {text.state === "missing" && (
        <div class="nothing">
          <ToucanEmptyNote>
            Sorry, the article text isn&apos;t available on this device.
          </ToucanEmptyNote>
          <OpenOriginal item={item} />
        </div>
      )}
      {text.state === "ready" ? (
        // Sanitised by `readingText`.
        <div
          data-text="ready"
          dangerouslySetInnerHTML={{ __html: text.content }}
        />
      ) : text.state === "loading" ? (
        // Shown as the text is read from this device's blobs, or fetched from
        // the server.
        <div class="fetching" data-text="loading">
          <ToucanSpinner />
        </div>
      ) : (
        <div data-text="missing" />
      )}
    </div>
  );
}

/**
 * A PDF, drawn page after page down the screen.
 *
 * The file is fetched through the proxy each time rather than stored, since
 * a paper can exceed `MAX_BLOB_BYTES`. Page one at card size is stored.
 * Pages are appended as they draw, so the first is readable while later ones
 * rasterise, and leaving the page stops the work.
 */
function PdfPages({
  item,
  onSettled,
}: {
  item: Item;
  onSettled: (carriesTitle: boolean) => void;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "missing" | "ready">(
    "loading",
  );
  const source = item.fileUrl ?? item.url;
  useEffect(() => {
    const into = required(holder.current, "pages");
    // Each drawing gets its own box, created here: a file that changes under
    // an open reader starts a second drawing while the first is still
    // rasterising, and only the box created last is on the screen.
    const box = document.createElement("div");
    box.className = "sheets";
    into.replaceChildren(box);
    let showing = true;
    const draw = async (): Promise<void> => {
      const width = into.clientWidth;
      const { bytes, status } = await fetchPage(source);
      // A refusal's body is the site's error page, not the paper.
      if (status >= 400) {
        throw new Error(`The site answered ${String(status)}`);
      }
      const { shapes, pages } = await pdfSheets(bytes, width);
      // The pages' boxes are added before any page is drawn, so the pane is
      // the paper's full height at once: the saved place is reachable
      // straight away, and nothing moves while it is read.
      const sheets = shapes.map((shape) => {
        const sheet = document.createElement("div");
        sheet.className = "sheet";
        sheet.style.aspectRatio = String(shape);
        return sheet;
      });
      box.append(...sheets);
      setState("ready");
      // Page one has the paper's title, so the page doesn't show a heading.
      onSettled(true);
      let at = 0;
      for await (const canvas of pages) {
        if (!showing) {
          return;
        }
        required(sheets[at], "sheet").append(canvas);
        at += 1;
      }
    };
    // Setting state after unmounting is a no-op in Preact.
    void draw().catch((error: unknown) => {
      // The note on the page says only that it failed.
      console.error(`Toucan Reads: could not draw a PDF (${String(error)})`);
      setState("missing");
      // No page one, so the page shows the heading.
      onSettled(false);
    });
    return () => {
      showing = false;
      into.replaceChildren();
    };
    // `onSettled` is the page's own callback, stable for the life of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);
  return (
    <>
      {state === "missing" && (
        <div class="nothing">
          <ToucanEmptyNote>
            {nothingSaid(item, "Sorry, this PDF couldn't be opened.")}
          </ToucanEmptyNote>
          <OpenOriginal item={item} />
        </div>
      )}
      {/* A paper is fetched whole and parsed before the first page draws. */}
      {state === "loading" && (
        <div class="fetching">
          <ToucanSpinner />
        </div>
      )}
      <div ref={holder} class="pages" data-pdf={state} />
    </>
  );
}

/**
 * Whether this reading frames the site. Only a link does, and not a paywalled
 * one, one that refuses framing or would not be read at all, or one on this
 * origin. The page's measure follows from this. See the `data-framed` rule.
 */
function framed(item: Item): boolean {
  return (
    item.kind === "link" &&
    item.paywalled !== true &&
    item.unframeable !== true &&
    item.refused !== true &&
    framableHere(item.url, location.origin)
  );
}

/**
 * A link's page itself, in a frame, since a link has nothing to extract.
 *
 * A browser draws a refused frame as a blank box without telling the page, so
 * the headers the fetch saw are the only warning. Where they refuse, or the
 * site would not be read at all, only the link to the site is offered.
 */
function SiteFrame({ item }: { item: Item }) {
  // A paywalled page framed here would show only the teaser and subscribe
  // form, so the link to the archived snapshot is offered instead.
  if (item.paywalled === true) {
    return (
      <div class="nothing">
        <ToucanEmptyNote>
          Sorry, this article requires a subscription — archive.today may have a
          copy you can read.
        </ToucanEmptyNote>
        <OpenOriginal item={item} />
      </div>
    );
  }
  // The site refuses framing or would not be read at all. Or it is on this
  // origin, where the frame's sandbox is no sandbox (see `framableHere`).
  if (!framed(item)) {
    return (
      <div class="nothing">
        <ToucanEmptyNote>
          {nothingSaid(item, "Sorry, this web page can't be embedded here.")}
        </ToucanEmptyNote>
        <OpenOriginal item={item} />
      </div>
    );
  }
  return (
    <iframe
      class="frame"
      src={item.url}
      title={item.title}
      referrerpolicy="no-referrer"
      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
    />
  );
}

/**
 * Reading a saved item, whichever kind it is: an article's extracted text, a
 * PDF's pages, or the site in a frame, all with the same controls.
 */
export function ReadPage({ item }: { item: Item }) {
  const page = useRef<HTMLDivElement>(null);
  const [settled, setSettled] = useState(false);
  // Whether the settled reading shows its own title: only a paper that
  // opened does, on page one.
  const [selfTitled, setSelfTitled] = useState(false);
  const { id, kind } = item;
  const onSettled = useCallback((carriesTitle: boolean) => {
    setSelfTitled(carriesTitle);
    setSettled(true);
  }, []);

  // The article's stored text, loaded here rather than in its body so the
  // lead picture can be checked against it. Setting state after unmounting is
  // a no-op in Preact.
  const [text, setText] = useState<Loaded<string>>({ state: "loading" });
  useEffect(() => {
    if (kind !== "article") {
      return;
    }
    const { textHash } = item;
    if (textHash === undefined) {
      setText({ state: "missing" });
      // Extraction strips the site's heading, so an article never shows its
      // own title.
      onSettled(false);
      return;
    }
    void readingText(textHash, item.url).then((cleaned) => {
      setText(
        cleaned === undefined
          ? { state: "missing" }
          : { state: "ready", content: cleaned },
      );
      onSettled(false);
    });
    // `onSettled` is stable. The hash decides whether there is text to fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, item.textHash]);

  // The picture above the reading is for articles only, and not when the text
  // already contains it. Shown only once that is known, so it never appears
  // and then vanishes.
  const leadSrc =
    kind === "article" &&
    item.thumbUrl !== undefined &&
    (text.state === "missing" ||
      (text.state === "ready" && !readingCarries(text.content, item.thumbUrl)))
      ? item.thumbUrl
      : undefined;

  // Where this device left off, restored once there is something to scroll and
  // recorded as it moves. A framed site scrolls inside its own cross-origin
  // box, so it doesn't keep a place. A layout effect, so the top of the
  // article is never painted before the jump.
  //
  // Scrolling to `READ_INTO` marks the item read; opening it and leaving
  // straight away does not.
  useLayoutEffect(() => {
    if (!settled) {
      return undefined;
    }
    const main = required(page.current, "page").closest("main");
    const scroller = required(main, "main");
    // The pane keeps growing as pages and pictures land, so `restoreScroll`
    // re-applies the offset until the height holds still. Until then scroll
    // events are the restore's own, and recording one would save a clamped
    // offset and mark the item read.
    const restore = restoreScroll(scroller, placeIn(id).top);
    // Shared so the shell's retracting bar ignores the restore's scrolls too.
    notePlacing(restore);
    // Once per open, since the read time is in the synced document.
    let noted = false;
    const note = (): void => {
      if (!noted) {
        noted = true;
        markRead(item, Date.now());
      }
    };
    const onScroll = (): void => {
      if (restore.restoring) {
        return;
      }
      const range = scroller.scrollHeight - scroller.clientHeight;
      const top = scroller.scrollTop;
      const through = range > 0 ? Math.min(top / range, 1) : 0;
      rememberPlace(id, { top, through });
      if (through >= READ_INTO) {
        note();
      }
    };
    scroller.addEventListener("scroll", onScroll);
    // A reading that fits the window fires no scroll event, so it is marked
    // read when all of it is on screen. Checked once the restore has stopped,
    // since before its pictures load any reading fits.
    let frame = requestAnimationFrame(function ask() {
      if (restore.restoring) {
        frame = requestAnimationFrame(ask);
        return;
      }
      // A pane with no height has had nothing drawn in it yet.
      if (
        scroller.clientHeight > 0 &&
        scroller.scrollHeight <= scroller.clientHeight
      ) {
        note();
      }
    });
    return () => {
      cancelAnimationFrame(frame);
      restore.stop();
      notePlacing(undefined);
      scroller.removeEventListener("scroll", onScroll);
    };
    // `item` is a new store reference on every render, so this depends on `id`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, settled]);

  // "y" archives the item and returns to the library, like the Archive button,
  // which shows the key once the keyboard is in use. Bound on every render,
  // since the item changes under it.
  const keyed = useKeyboardInUse();
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "y" || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      if (isArchived(item)) {
        return;
      }
      event.preventDefault();
      archiveItem(item, Date.now());
      go(libraryPath());
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  });

  const bodies: Record<Item["kind"], () => JSX.Element> = {
    article: () => <ArticleText item={item} text={text} />,
    pdf: () => <PdfPages item={item} onSettled={onSettled} />,
    link: () => <SiteFrame item={item} />,
  };
  // Whether the reading has settled (a frame always has), so the heading and
  // the finishing rule are not drawn together over an empty pane and then
  // pushed apart when the content arrives.
  const drawn = kind === "link" || settled;

  // Whether the reading shows its own title, so a heading would repeat it: a
  // paper's page one, or a framed site. An unframed link or a paper that would
  // not open gets the heading.
  const titled = selfTitled || framed(item);

  // The byline has the site, publication date and reading time. The saved,
  // read and filed dates are on the edit page.
  const about = [
    siteFromUrl(item.url),
    item.publishedAt === undefined
      ? undefined
      : publishedLabel(item.publishedAt),
    itemReadingTime(item),
  ].filter((part) => part !== undefined);
  return (
    <Page data-kind={kind} data-framed={framed(item) ? "" : undefined}>
      {leadSrc !== undefined && (
        <img
          class="lead"
          src={leadSrc}
          alt=""
          loading="lazy"
          referrerpolicy="no-referrer"
        />
      )}
      {/* The heading, for readings without their own title. See `titled`. */}
      <div ref={page} class="headline">
        {drawn && !titled && (
          <>
            <h1>{item.title}</h1>
            <p class="byline">{about.join(" · ")}</p>
          </>
        )}
      </div>
      {bodies[kind]()}
      {drawn && (
        <div class="finish">
          {item.note !== undefined && (
            <p class="noted">
              <span class="noted-marker">Note: </span>
              <span
                // `noteHtml` makes links in the note tappable, as elsewhere.
                dangerouslySetInnerHTML={{ __html: noteHtml(item.note) }}
              />
            </p>
          )}
          <div class="finish-actions">
            {!isArchived(item) && (
              <ToucanButton
                variant="primary"
                onClick={() => {
                  archiveItem(item, Date.now());
                  go(libraryPath());
                }}
              >
                Archive
                {keyed && <ToucanKeyCap>Y</ToucanKeyCap>}
              </ToucanButton>
            )}
            <ToucanButton
              onClick={() => {
                go(`edit/${id}`);
              }}
            >
              Edit
            </ToucanButton>
          </div>
        </div>
      )}
    </Page>
  );
}
